import { BedrockService, GuardrailBlockedError } from 'src/components/das-finsuite/ea-huddle-v2/services/BedrockService';
import {
  buildDashboardNarrativePrompt,
  DASHBOARD_NARRATIVE_SCHEMA,
  DashboardNarrativeFields,
  DashboardNarrativePromptData,
  renderDashboardNarrative
} from './dashboardNarrativePrompt';

/**
 * Generates the dashboard-agnostic AI narrative for the KPI Metrics page by invoking
 * Bedrock directly from the browser via the shared `BedrockService` (no new IAM — the
 * Cognito role already permits it). Mirrors `OENarrativeService`'s tool-use pattern so
 * the response shape is enforced by the model rather than parsed out of free text.
 */

/** Output ceiling: a 3-5 sentence summary runs well under this. */
const NARRATIVE_MAX_TOKENS = 1024;

export class DashboardNarrativeService {
  /** Turn a Bedrock failure into an actionable message; guardrail blocks are detected by type. */
  private static toActionableError(err: unknown): Error {
    if (err instanceof GuardrailBlockedError) {
      const what = err.source === 'INPUT' ? 'The dashboard data' : 'The generated summary';
      return new Error(`${err.message} ${what} was blocked by the content filter.`);
    }
    return err instanceof Error ? err : new Error(String(err));
  }

  /**
   * Generate the summary for one dashboard over one reporting window.
   * Returns the rendered paragraph, or throws on failure / empty response.
   */
  public static async generateDashboardSummary(input: DashboardNarrativePromptData): Promise<string> {
    let fields: DashboardNarrativeFields;
    try {
      fields = await BedrockService.invokeWithSchema<DashboardNarrativeFields>(buildDashboardNarrativePrompt(input), DASHBOARD_NARRATIVE_SCHEMA, {
        maxTokens: NARRATIVE_MAX_TOKENS
      });
    } catch (err) {
      throw this.toActionableError(err);
    }

    const rendered = renderDashboardNarrative(fields);
    if (!rendered) {
      throw new Error('The model returned an empty summary. Try regenerating.');
    }
    return rendered;
  }
}
